/**
 * HostelGLJournals.jsx — FIN-01D + FIN-01F: GL Journal Maker-Checker Workspace
 * Route: /campus/hostel/gl/journals
 *
 * Provides institution-scoped journal listing, detail view, and lifecycle
 * actions (Validate → Approve → Post) with strict RBAC enforcement.
 * Maker-checker: validator ≠ approver enforced by backend.
 * FIN-01F: Exception-Authorization workflow for NULL-validator journals.
 */
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  ArrowLeft, BookOpen, CheckCircle2, Clock, RefreshCw,
  ChevronDown, ChevronRight, Filter, AlertCircle, Lock,
  TrendingUp, FileText, Eye, RotateCcw, ShieldAlert, ShieldCheck,
  UserCheck, UserX, ClipboardEdit,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ── RBAC helpers ──────────────────────────────────────────────────────────────
const CAN_VALIDATE = ["finance_manager", "cfo", "institution_admin", "super_admin"];
const CAN_APPROVE  = ["finance_manager", "cfo", "super_admin"];
const CAN_POST     = ["finance_manager", "cfo", "super_admin"];

// ── Status config ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  DRAFT:     { label: "Draft",     color: "bg-slate-500/20 text-slate-300 border-slate-500/30", dot: "bg-slate-400" },
  VALIDATED: { label: "Validated", color: "bg-blue-500/20 text-blue-300 border-blue-500/30",   dot: "bg-blue-400" },
  APPROVED:  { label: "Approved",  color: "bg-amber-500/20 text-amber-300 border-amber-500/30", dot: "bg-amber-400" },
  POSTED:    { label: "Posted",    color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", dot: "bg-emerald-400" },
  REVERSED:  { label: "Reversed",  color: "bg-red-500/20 text-red-300 border-red-500/30",      dot: "bg-red-400" },
  CANCELLED: { label: "Cancelled", color: "bg-red-500/20 text-red-300 border-red-500/30",      dot: "bg-red-400" },
};

const FILTERS = ["ALL", "DRAFT", "VALIDATED", "APPROVED", "POSTED"];

function fmtAmt(v) {
  const n = parseFloat(v || 0);
  return `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

// ── Journal lifecycle steps ───────────────────────────────────────────────────
const LIFECYCLE = ["DRAFT", "VALIDATED", "APPROVED", "POSTED"];

function LifecycleTimeline({ status }) {
  const cur = LIFECYCLE.indexOf(status);
  return (
    <div className="flex items-center gap-1 text-xs mt-2" data-testid="journal-lifecycle">
      {LIFECYCLE.map((s, i) => {
        const done    = i <= cur;
        const active  = i === cur;
        const sc      = STATUS_CONFIG[s] || {};
        return (
          <React.Fragment key={s}>
            <div className="flex flex-col items-center gap-0.5">
              <div className={`w-3 h-3 rounded-full border ${active ? sc.dot : done ? "bg-emerald-500" : "bg-slate-700"} border-slate-600`} />
              <span className={`${active ? "text-white font-semibold" : done ? "text-emerald-400" : "text-slate-600"}`}>
                {s.charAt(0) + s.slice(1).toLowerCase()}
              </span>
            </div>
            {i < LIFECYCLE.length - 1 && (
              <div className={`flex-1 h-px ${i < cur ? "bg-emerald-500/50" : "bg-slate-700"} mb-3`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

// ── Journal row ───────────────────────────────────────────────────────────────
function JournalRow({ journal, expanded, onToggle, onAction, userRole }) {
  const sc = STATUS_CONFIG[journal.status] || STATUS_CONFIG.DRAFT;
  const canValidate = CAN_VALIDATE.includes(userRole) && journal.status === "DRAFT";
  const canApprove  = CAN_APPROVE.includes(userRole)  && journal.status === "VALIDATED";
  const canPost     = CAN_POST.includes(userRole)      && journal.status === "APPROVED";

  return (
    <div className="border border-slate-700/50 rounded-xl overflow-hidden"
      data-testid={`journal-row-${journal.id}`}>
      {/* Header row */}
      <div
        className="flex items-center gap-4 px-4 py-3 bg-slate-800/60 hover:bg-slate-800/80 cursor-pointer transition-all"
        onClick={onToggle}
        data-testid={`journal-toggle-${journal.id}`}
      >
        <div className="text-slate-500">
          {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-mono text-white font-medium">
              {journal.reference_number}
            </span>
            <Badge className={`text-xs border ${sc.color}`}>{sc.label}</Badge>
            {journal.accounting_event_type && (
              <Badge className="text-xs border bg-purple-500/10 text-purple-300 border-purple-500/20">
                {journal.accounting_event_type.replace(/_/g, " ")}
              </Badge>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-0.5">
            {journal.journal_type} · {fmtDate(journal.accounting_date)}
          </p>
        </div>
        <div className="text-right hidden md:block">
          <p className="text-sm font-medium text-amber-300">{fmtAmt(journal.total_debit_amount)} DR</p>
          <p className="text-xs text-slate-400">{fmtAmt(journal.total_credit_amount)} CR</p>
        </div>
        <div className="flex gap-2 ml-2">
          {canValidate && (
            <Button size="sm" data-testid={`btn-validate-${journal.id}`}
              className="bg-blue-700 hover:bg-blue-600 text-white text-xs px-3 py-1 h-7"
              onClick={(e) => { e.stopPropagation(); onAction(journal.id, "validate"); }}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> Validate
            </Button>
          )}
          {canApprove && (
            <Button size="sm" data-testid={`btn-approve-${journal.id}`}
              className="bg-amber-700 hover:bg-amber-600 text-white text-xs px-3 py-1 h-7"
              onClick={(e) => { e.stopPropagation(); onAction(journal.id, "approve"); }}>
              <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
            </Button>
          )}
          {canPost && (
            <Button size="sm" data-testid={`btn-post-${journal.id}`}
              className="bg-emerald-700 hover:bg-emerald-600 text-white text-xs px-3 py-1 h-7"
              onClick={(e) => { e.stopPropagation(); onAction(journal.id, "post"); }}>
              <TrendingUp className="w-3 h-3 mr-1" /> Post
            </Button>
          )}
        </div>
      </div>

      {/* Expanded detail */}
      {expanded && <JournalDetail journalId={journal.id} />}
    </div>
  );
}

// ── Journal detail panel ──────────────────────────────────────────────────────
function JournalDetail({ journalId }) {
  const { token } = useAuth();
  const [detail, setDetail]             = useState(null);
  const [loading, setLoading]           = useState(true);
  const [excFullyAuthed, setExcFullyAuthed] = useState(false);

  const fetchDetail = useCallback(() => {
    axios.get(`${API}/api/finance/gl/journals/${journalId}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { setDetail(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [journalId, token]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);

  const needsException = detail?.status === "VALIDATED"
    && !detail?.validated_by && !detail?.validated_by_party_id;

  if (loading) return (
    <div className="px-6 py-4 bg-slate-900/40 text-slate-400 text-sm animate-pulse">
      Loading journal detail…
    </div>
  );
  if (!detail) return (
    <div className="px-6 py-4 bg-slate-900/40 text-red-400 text-sm">
      Could not load journal detail.
    </div>
  );

  return (
    <div className="bg-slate-900/40 border-t border-slate-700/40 px-5 py-4 space-y-4"
      data-testid={`journal-detail-${journalId}`}>
      {/* Lifecycle timeline */}
      <LifecycleTimeline status={detail.status} />

      {/* Actors */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <ActorCell label="Validated By" value={detail.validated_by} date={detail.validated_at} />
        <ActorCell label="Approved By"  value={detail.approved_by}  date={detail.approved_at} />
        <ActorCell label="Posted By"    value={detail.posted_by}    date={detail.posted_at} />
        <ActorCell label="Journal Type" value={detail.journal_type} />
      </div>

      {/* NULL validator warning */}
      {needsException && !excFullyAuthed && (
        <div className="flex items-start gap-2 bg-orange-900/20 border border-orange-500/30 rounded-lg px-3 py-2 text-xs"
          data-testid="null-validator-warning">
          <ShieldAlert className="w-4 h-4 text-orange-400 shrink-0 mt-0.5" />
          <div>
            <p className="text-orange-300 font-medium">Validator identity unrecoverable (Defect D3)</p>
            <p className="text-orange-200/70">
              This journal was validated before validator email capture was deployed.
              Complete the <strong>Exception Authorization</strong> workflow below before approval is permitted.
            </p>
          </div>
        </div>
      )}

      {/* Exception Authorization Panel */}
      {needsException && (
        <ExceptionPanel
          journalId={journalId}
          onExceptionFullyAuthorized={() => { setExcFullyAuthed(true); fetchDetail(); }}
        />
      )}

      {/* Lines table */}
      {detail.lines && detail.lines.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="journal-lines-table">
            <thead>
              <tr className="border-b border-slate-700/50">
                {["Account", "Description", "Type", "Debit", "Credit"].map((h) => (
                  <th key={h} className="text-left py-2 px-3 text-slate-400 font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60">
              {detail.lines.map((l) => (
                <tr key={l.id} data-testid={`journal-line-${l.id}`}
                  className="hover:bg-slate-800/30">
                  <td className="py-2 px-3">
                    <span className="font-mono text-amber-300">{l.account_code}</span>
                    <span className="text-slate-400 ml-1">{l.account_name}</span>
                  </td>
                  <td className="py-2 px-3 text-slate-300 max-w-xs truncate">{l.description || "—"}</td>
                  <td className="py-2 px-3">
                    <Badge className="text-xs border bg-slate-700/40 text-slate-300 border-slate-600/40">
                      {l.line_type}
                    </Badge>
                  </td>
                  <td className="py-2 px-3 text-right font-medium">
                    {parseFloat(l.debit_amount || 0) > 0
                      ? <span className="text-red-300">{fmtAmt(l.debit_amount)}</span>
                      : <span className="text-slate-600">—</span>}
                  </td>
                  <td className="py-2 px-3 text-right font-medium">
                    {parseFloat(l.credit_amount || 0) > 0
                      ? <span className="text-emerald-300">{fmtAmt(l.credit_amount)}</span>
                      : <span className="text-slate-600">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-slate-700/50 bg-slate-800/40">
                <td colSpan={3} className="py-2 px-3 text-slate-400 text-right font-semibold">Totals</td>
                <td className="py-2 px-3 text-right font-bold text-red-300">
                  {fmtAmt(detail.lines.reduce((s, l) => s + parseFloat(l.debit_amount || 0), 0))}
                </td>
                <td className="py-2 px-3 text-right font-bold text-emerald-300">
                  {fmtAmt(detail.lines.reduce((s, l) => s + parseFloat(l.credit_amount || 0), 0))}
                </td>
              </tr>
            </tfoot>
          </table>
          {detail.is_balanced && (
            <p className="text-xs text-emerald-400 mt-2 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Journal is balanced
            </p>
          )}
        </div>
      ) : (
        <p className="text-slate-500 text-sm">No journal lines found.</p>
      )}

      {/* Description */}
      {detail.description && (
        <p className="text-xs text-slate-400 italic border-t border-slate-700/30 pt-2">
          Note: {detail.description}
        </p>
      )}
    </div>
  );
}

function ActorCell({ label, value, date }) {
  return (
    <div className="bg-slate-800/40 rounded-lg p-2.5 border border-slate-700/30">
      <p className="text-slate-500 text-xs mb-0.5">{label}</p>
      <p className="text-slate-200 text-xs font-medium truncate">{value || "—"}</p>
      {date && <p className="text-slate-500 text-xs">{fmtDate(date)}</p>}
    </div>
  );
}

// ── Exception Status config ───────────────────────────────────────────────────
const EXC_STATUS_CONFIG = {
  PENDING_AUTHORIZATION:  { label: "Pending 1st Auth",     color: "bg-orange-500/20 text-orange-300 border-orange-500/30" },
  PARTIALLY_AUTHORIZED:   { label: "Awaiting Co-Auth",     color: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30" },
  FULLY_AUTHORIZED:       { label: "Fully Authorized",     color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30" },
  REJECTED:               { label: "Rejected",             color: "bg-red-500/20 text-red-300 border-red-500/30" },
};

// ── ExceptionPanel ─────────────────────────────────────────────────────────────
function ExceptionPanel({ journalId, onExceptionFullyAuthorized }) {
  const { token } = useAuth();
  const [exc, setExc]           = useState(null);
  const [needsExc, setNeedsExc] = useState(false);
  const [loading, setLoading]   = useState(true);
  const [step, setStep]         = useState(null); // "create"|"authorize"|"co-authorize"|"reject"
  const [form, setForm]         = useState({
    exception_code: `FIN-01F-HOB-0002-EXCEPTION-001`,
    root_cause_description: "",
    supporting_evidence: "",
    attestation: "",
    review_note: "",
    rejection_reason: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const fetchExc = useCallback(() => {
    if (!token) return;
    setLoading(true);
    axios.get(`${API}/api/finance/gl/journals/${journalId}/exception`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then((r) => {
      setExc(r.data.exception);
      setNeedsExc(r.data.needs_exception_authorization);
      setLoading(false);
      if (r.data.exception?.status === "FULLY_AUTHORIZED") {
        onExceptionFullyAuthorized?.();
      }
    }).catch(() => setLoading(false));
  }, [token, journalId, onExceptionFullyAuthorized]);

  useEffect(() => { fetchExc(); }, [fetchExc]);

  const handleSubmit = async (action) => {
    setSubmitting(true);
    const urls = {
      create: `/api/finance/gl/journals/${journalId}/exception`,
      authorize: `/api/finance/gl/journals/${journalId}/exception/authorize`,
      "co-authorize": `/api/finance/gl/journals/${journalId}/exception/co-authorize`,
      reject: `/api/finance/gl/journals/${journalId}/exception/reject`,
    };
    const payloads = {
      create: {
        exception_code: form.exception_code,
        root_cause_description: form.root_cause_description,
        supporting_evidence: form.supporting_evidence || undefined,
      },
      authorize: { attestation: form.attestation },
      "co-authorize": { review_note: form.review_note },
      reject: { rejection_reason: form.rejection_reason },
    };
    try {
      await axios.post(`${API}${urls[action]}`, payloads[action], {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Exception ${action} completed successfully`);
      setStep(null);
      fetchExc();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (detail || e.message);
      toast.error(msg || `Exception action failed`);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="text-slate-500 text-xs animate-pulse py-2">Loading exception status…</div>
  );
  if (!needsExc) return null;

  const excStatus = exc?.status;
  const statusCfg = EXC_STATUS_CONFIG[excStatus] || {};

  return (
    <div className="border border-orange-500/30 rounded-xl bg-orange-500/5 p-4 space-y-3"
      data-testid="exception-panel">
      {/* Header */}
      <div className="flex items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-orange-400" />
        <span className="text-sm font-semibold text-orange-300">
          Exception Authorization Required
        </span>
        {excStatus && (
          <Badge className={`text-xs border ml-auto ${statusCfg.color}`}>
            {statusCfg.label}
          </Badge>
        )}
      </div>

      <p className="text-xs text-orange-200/70">
        This journal was validated before the email-capture fix was deployed (Defect D3, 2026-10-30).
        The original validator's identity is permanently unrecoverable. A dual-authorized exception
        record is required before approval may proceed.
      </p>

      {/* Step indicators */}
      <div className="flex items-center gap-2 text-xs">
        {[
          { key: "create",       label: "1. Create Record", done: !!exc },
          { key: "authorize",    label: "2. FM Attest",     done: ["PARTIALLY_AUTHORIZED","FULLY_AUTHORIZED"].includes(excStatus) },
          { key: "co-authorize", label: "3. Co-Authorize",  done: excStatus === "FULLY_AUTHORIZED" },
        ].map((s, i) => (
          <React.Fragment key={s.key}>
            <div className={`flex items-center gap-1 px-2 py-1 rounded-md border ${
              s.done
                ? "bg-emerald-500/10 border-emerald-500/30 text-emerald-400"
                : "bg-slate-800/40 border-slate-600/30 text-slate-400"
            }`}>
              {s.done
                ? <CheckCircle2 className="w-3 h-3" />
                : <Clock className="w-3 h-3" />}
              {s.label}
            </div>
            {i < 2 && <span className="text-slate-600">→</span>}
          </React.Fragment>
        ))}
      </div>

      {/* Exception detail if exists */}
      {exc && (
        <div className="space-y-2 text-xs bg-slate-900/40 rounded-lg p-3 border border-slate-700/30">
          <p className="font-medium text-slate-300">
            Exception Code: <span className="font-mono text-amber-300">{exc.exception_code}</span>
          </p>
          {exc.root_cause_description && (
            <p className="text-slate-400"><span className="text-slate-300 font-medium">Root Cause:</span> {exc.root_cause_description}</p>
          )}
          {exc.authorizer_1_email && (
            <p className="text-slate-400 flex items-center gap-1">
              <UserCheck className="w-3 h-3 text-emerald-400" />
              <span className="text-slate-300 font-medium">FM1:</span> {exc.authorizer_1_email}
              <span className="text-slate-600 ml-1">@ {fmtDate(exc.authorized_1_at)}</span>
            </p>
          )}
          {exc.authorizer_2_email && (
            <p className="text-slate-400 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3 text-emerald-400" />
              <span className="text-slate-300 font-medium">Co-Auth:</span> {exc.authorizer_2_email}
              <span className="text-slate-600 ml-1">@ {fmtDate(exc.authorized_2_at)}</span>
            </p>
          )}
          {exc.rejected_by_email && (
            <p className="text-red-400 flex items-center gap-1">
              <UserX className="w-3 h-3" />
              Rejected by {exc.rejected_by_email}: {exc.rejection_reason}
            </p>
          )}
        </div>
      )}

      {/* Action buttons */}
      {excStatus !== "FULLY_AUTHORIZED" && excStatus !== "REJECTED" && (
        <div className="flex gap-2 flex-wrap">
          {!exc && (
            <Button size="sm" data-testid="btn-exception-create"
              className="bg-orange-700 hover:bg-orange-600 text-white text-xs px-3 py-1 h-7"
              onClick={() => setStep(step === "create" ? null : "create")}>
              <ClipboardEdit className="w-3 h-3 mr-1" /> Create Exception Record
            </Button>
          )}
          {excStatus === "PENDING_AUTHORIZATION" && (
            <Button size="sm" data-testid="btn-exception-authorize"
              className="bg-blue-700 hover:bg-blue-600 text-white text-xs px-3 py-1 h-7"
              onClick={() => setStep(step === "authorize" ? null : "authorize")}>
              <UserCheck className="w-3 h-3 mr-1" /> First Authorization (FM Attestation)
            </Button>
          )}
          {excStatus === "PARTIALLY_AUTHORIZED" && (
            <Button size="sm" data-testid="btn-exception-co-authorize"
              className="bg-amber-700 hover:bg-amber-600 text-white text-xs px-3 py-1 h-7"
              onClick={() => setStep(step === "co-authorize" ? null : "co-authorize")}>
              <ShieldCheck className="w-3 h-3 mr-1" /> Co-Authorize (FM2 / CFO)
            </Button>
          )}
          {exc && (
            <Button size="sm" variant="ghost" data-testid="btn-exception-reject"
              className="text-red-400 hover:text-red-300 text-xs px-2 py-1 h-7"
              onClick={() => setStep(step === "reject" ? null : "reject")}>
              <UserX className="w-3 h-3 mr-1" /> Reject
            </Button>
          )}
        </div>
      )}

      {excStatus === "FULLY_AUTHORIZED" && (
        <div className="flex items-center gap-2 text-xs text-emerald-400 bg-emerald-500/10 rounded-lg px-3 py-2 border border-emerald-500/20"
          data-testid="exception-authorized-banner">
          <ShieldCheck className="w-4 h-4" />
          Exception fully authorized. Journal is now eligible for approval.
          Use the <strong>Approve</strong> button above to proceed.
        </div>
      )}

      {/* Inline forms */}
      {step === "create" && (
        <ExcForm
          title="Create Exception Record"
          fields={[
            { key: "exception_code",          label: "Exception Code",        required: true,  placeholder: "FIN-01F-HOB-0002-EXCEPTION-001" },
            { key: "root_cause_description",  label: "Root Cause (20+ chars)", required: true, placeholder: "Journal validated before email-capture fix…", multiline: true },
            { key: "supporting_evidence",     label: "Supporting Evidence",   required: false, placeholder: "Defect D3, migration 0046, timestamp 05:10:44Z…", multiline: true },
          ]}
          form={form} setForm={setForm}
          onSubmit={() => handleSubmit("create")}
          onCancel={() => setStep(null)}
          submitting={submitting}
          testId="exc-form-create"
        />
      )}
      {step === "authorize" && (
        <ExcForm
          title="First Authorization — FM Attestation"
          subtitle="Attest that you did NOT validate this journal and that approval is authorized on the basis of verified business correctness."
          fields={[
            { key: "attestation", label: "Attestation Statement (20+ chars)", required: true,
              placeholder: "I confirm that I did not validate journal JRNL-HOSTEL-HOB-202602-0002 on or before 2026-10-30T05:26:24Z…",
              multiline: true },
          ]}
          form={form} setForm={setForm}
          onSubmit={() => handleSubmit("authorize")}
          onCancel={() => setStep(null)}
          submitting={submitting}
          testId="exc-form-authorize"
        />
      )}
      {step === "co-authorize" && (
        <ExcForm
          title="Co-Authorization — FM2 / CFO Independent Review"
          subtitle="A DIFFERENT Finance Manager or CFO must independently review and co-authorize."
          fields={[
            { key: "review_note", label: "Review Note (20+ chars)", required: true,
              placeholder: "Independently reviewed journal lines. DR=CR=44,957.29. Account mapping verified…",
              multiline: true },
          ]}
          form={form} setForm={setForm}
          onSubmit={() => handleSubmit("co-authorize")}
          onCancel={() => setStep(null)}
          submitting={submitting}
          testId="exc-form-co-authorize"
        />
      )}
      {step === "reject" && (
        <ExcForm
          title="Reject Exception Authorization"
          subtitle="Rejection is permanent. Escalate to senior financial authority before creating a new exception."
          fields={[
            { key: "rejection_reason", label: "Rejection Reason (10+ chars)", required: true,
              placeholder: "Rejected because…", multiline: true },
          ]}
          form={form} setForm={setForm}
          onSubmit={() => handleSubmit("reject")}
          onCancel={() => setStep(null)}
          submitting={submitting}
          submitLabel="Reject Exception"
          submitClass="bg-red-700 hover:bg-red-600"
          testId="exc-form-reject"
        />
      )}
    </div>
  );
}

// ── ExcForm helper ─────────────────────────────────────────────────────────────
function ExcForm({ title, subtitle, fields, form, setForm, onSubmit, onCancel,
  submitting, submitLabel = "Submit", submitClass = "bg-blue-700 hover:bg-blue-600", testId }) {
  return (
    <div className="bg-slate-900/60 rounded-lg p-3 border border-slate-700/40 space-y-3"
      data-testid={testId}>
      <p className="text-xs font-semibold text-slate-200">{title}</p>
      {subtitle && <p className="text-xs text-slate-400">{subtitle}</p>}
      {fields.map((f) => (
        <div key={f.key}>
          <label className="text-xs text-slate-400 mb-0.5 block">{f.label}</label>
          {f.multiline ? (
            <textarea
              data-testid={`exc-input-${f.key}`}
              value={form[f.key] || ""}
              onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              rows={3}
              className="w-full bg-slate-800/60 border border-slate-600/50 rounded-lg px-3 py-2
                text-xs text-slate-200 placeholder-slate-600 resize-none focus:outline-none
                focus:border-blue-500/60"
            />
          ) : (
            <input
              data-testid={`exc-input-${f.key}`}
              value={form[f.key] || ""}
              onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))}
              placeholder={f.placeholder}
              className="w-full bg-slate-800/60 border border-slate-600/50 rounded-lg px-3 py-1.5
                text-xs text-slate-200 placeholder-slate-600 focus:outline-none focus:border-blue-500/60"
            />
          )}
        </div>
      ))}
      <div className="flex gap-2 pt-1">
        <Button size="sm" disabled={submitting}
          className={`text-white text-xs px-4 py-1 h-7 ${submitClass}`}
          data-testid={`${testId}-submit`}
          onClick={onSubmit}>
          {submitting ? "Submitting…" : submitLabel}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel}
          className="text-slate-400 hover:text-white text-xs h-7"
          data-testid={`${testId}-cancel`}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// ── Stats bar ─────────────────────────────────────────────────────────────────
function StatsBar({ journals }) {
  const counts = journals.reduce((acc, j) => {
    acc[j.status] = (acc[j.status] || 0) + 1;
    return acc;
  }, {});
  return (
    <div className="flex gap-4 flex-wrap text-xs" data-testid="journals-stats-bar">
      {["DRAFT", "VALIDATED", "APPROVED", "POSTED"].map((s) => {
        const sc = STATUS_CONFIG[s];
        return (
          <div key={s} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border ${sc.color}`}>
            <div className={`w-2 h-2 rounded-full ${sc.dot}`} />
            <span>{sc.label}: <strong>{counts[s] || 0}</strong></span>
          </div>
        );
      })}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function HostelGLJournals() {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const refFilter = searchParams.get("ref") || "";

  const [journals, setJournals]     = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [statusFilter, setStatus]   = useState("ALL");
  const [expanded, setExpanded]     = useState(null);
  const [acting, setActing]         = useState(null);

  const fetchJournals = useCallback(() => {
    if (!token) return;
    setLoading(true);
    const params = { limit: 100 };
    if (statusFilter !== "ALL") params.status = statusFilter;
    axios.get(`${API}/api/finance/gl/journals`, {
      headers: { Authorization: `Bearer ${token}` },
      params,
    })
      .then((r) => {
        let list = r.data.journals || [];
        if (refFilter) {
          list = list.filter((j) =>
            j.reference_number?.toLowerCase().includes(refFilter.toLowerCase())
          );
        }
        setJournals(list);
        setLoading(false);
        setError(null);
      })
      .catch((e) => {
        const msg = e.response?.data?.detail?.message || e.response?.data?.detail || e.message;
        setError(msg);
        setLoading(false);
      });
  }, [token, statusFilter, refFilter]);

  useEffect(() => { fetchJournals(); }, [fetchJournals]);

  const handleAction = async (journalId, action) => {
    setActing(journalId + action);
    const endpoint = `${API}/api/finance/gl/journals/${journalId}/${action}`;
    try {
      await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
      const labels = { validate: "Validated", approve: "Approved", post: "Posted to GL" };
      toast.success(`Journal ${labels[action] || action} successfully`);
      fetchJournals();
      setExpanded(null);
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" ? detail.message : (detail || e.message);
      toast.error(msg || `Action failed: ${action}`);
    } finally {
      setActing(null);
    }
  };

  const userRole = user?.role || "";
  const canAnyAction = CAN_VALIDATE.includes(userRole);

  return (
    <div className="p-6 space-y-6 max-w-6xl mx-auto" data-testid="hostel-gl-journals-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/campus/hostel")}
            className="text-slate-400 hover:text-white" data-testid="btn-back-hostel">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-amber-400" />
              <h1 className="text-xl font-bold text-white">GL Journals</h1>
              <Badge className="text-xs border bg-amber-500/10 text-amber-300 border-amber-500/20">
                Hostel · FIN-01D
              </Badge>
            </div>
            <p className="text-slate-400 text-sm mt-0.5">
              Maker-Checker Approval Workspace · Kaveri Bhavan, VCE
            </p>
          </div>
        </div>
        <Button
          variant="ghost" size="sm"
          className="text-slate-400 hover:text-white"
          onClick={fetchJournals}
          data-testid="btn-refresh-journals"
        >
          <RefreshCw className="w-4 h-4 mr-1" /> Refresh
        </Button>
      </div>

      {/* RBAC notice */}
      {!canAnyAction && (
        <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-800/40
          border border-slate-700/40 rounded-lg px-3 py-2"
          data-testid="rbac-notice">
          <Lock className="w-3 h-3" />
          Read-only view. Journal actions require Finance Manager, CFO, or Administrator role.
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap" data-testid="journal-filters">
        <Filter className="w-4 h-4 text-slate-400" />
        {FILTERS.map((f) => (
          <button
            key={f}
            data-testid={`filter-${f.toLowerCase()}`}
            onClick={() => { setStatus(f); setExpanded(null); }}
            className={`text-xs px-3 py-1.5 rounded-lg border transition-all
              ${statusFilter === f
                ? "bg-amber-600/80 text-white border-amber-500/50"
                : "bg-slate-800/60 text-slate-400 border-slate-700/40 hover:border-slate-600/60 hover:text-slate-200"
              }`}
          >
            {f === "ALL" ? "All Statuses" : f.charAt(0) + f.slice(1).toLowerCase()}
          </button>
        ))}
        {refFilter && (
          <span className="text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 px-2 py-1 rounded-lg">
            Filtered: {refFilter}
          </span>
        )}
      </div>

      {/* Stats */}
      {journals.length > 0 && <StatsBar journals={journals} />}

      {/* Content */}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-12 justify-center"
          data-testid="journals-loading">
          <RefreshCw className="w-4 h-4 animate-spin" /> Loading journals…
        </div>
      ) : error ? (
        <div className="text-red-400 text-sm p-4 bg-red-900/10 border border-red-500/20 rounded-xl"
          data-testid="journals-error">
          <AlertCircle className="w-4 h-4 inline mr-2" />
          {error}
        </div>
      ) : journals.length === 0 ? (
        <div className="text-center py-16 text-slate-500" data-testid="journals-empty">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p className="text-sm">No journals found
            {statusFilter !== "ALL" ? ` with status "${statusFilter}"` : ""}.
          </p>
          <p className="text-xs mt-1 text-slate-600">
            Generate invoices for confirmed hostel bookings to create GL journals.
          </p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="journals-list">
          {journals.map((j) => (
            <JournalRow
              key={j.id}
              journal={j}
              expanded={expanded === j.id}
              onToggle={() => setExpanded(expanded === j.id ? null : j.id)}
              onAction={(jid, action) => !acting && handleAction(jid, action)}
              userRole={userRole}
            />
          ))}
        </div>
      )}

      {/* Maker-checker guidance */}
      {canAnyAction && journals.length > 0 && (
        <div className="text-xs text-slate-500 border border-slate-700/30 rounded-lg p-3 space-y-1"
          data-testid="maker-checker-guidance">
          <p className="text-slate-400 font-medium flex items-center gap-1">
            <AlertCircle className="w-3 h-3" /> Maker-Checker Rules
          </p>
          <p>• The person who <strong>validates</strong> a journal cannot be the same person who <strong>approves</strong> it.</p>
          <p>• Only Finance Managers, CFOs, and Super Admins can Approve and Post journals.</p>
          <p>• Institution Admins can Validate but not Approve or Post.</p>
          <p>• Once POSTED, a journal is immutable and cannot be changed.</p>
        </div>
      )}
    </div>
  );
}
